import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, MessageSquare, Phone, Calendar, CheckSquare, Reply, Sparkles, Mail, Eye } from "lucide-react";
import { useSignature, previewHtml, TONES } from "@/lib/emailCompose";
import { useAuth } from "@/context/AuthContext";

const MODES = [
  { key: "reply", label: "Reply", Icon: Reply, needsReply: true, placeholder: "Write a reply — it sends to the client and lands right here" },
  { key: "logmail", label: "Log email", Icon: Mail, needsReply: true, placeholder: "Log an email you already sent (e.g. from Outlook) — clears the reply nag" },
  { key: "note", label: "Note", Icon: MessageSquare, placeholder: "Add a note for your team…" },
  { key: "call", label: "Call", Icon: Phone, placeholder: "Log a call — what was discussed…" },
  { key: "meeting", label: "Meeting", Icon: Calendar, placeholder: "Log a meeting…" },
  { key: "task", label: "Task", Icon: CheckSquare, placeholder: "Add a follow-up task…" },
];

// One composer where every input — a reply, note, logged call/meeting or task — lands straight
// into this project's context. Nothing to chase across other screens: this is the one-stop shop.
export default function ContextComposer({ bidId, canSend, replyTarget, onPosted, onReconnect }) {
  const { user: me } = useAuth();
  const claudeAssistant = !!me?.features?.assistant_claude;
  const available = MODES.filter((m) => !m.needsReply || replyTarget);
  const [mode, setMode] = useState(replyTarget ? "reply" : "note");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [tone, setTone] = useState("warm");
  const [showPreview, setShowPreview] = useState(false);
  const sig = useSignature();
  const active = available.find((m) => m.key === mode) || available[0];

  const draftWithSol = async (toneKey = tone) => {
    if (!replyTarget || drafting) return;
    setTone(toneKey); setDrafting(true);
    try {
      const r = await api.post(`/email/events/${replyTarget.id}/draft`, null, { params: { tone: toneKey } });
      setText(r.data.draft || "");
      if (!r.data.draft) toast.message("Nothing to add — write your reply below.");
    } catch { toast.error("Couldn't draft a reply"); }
    finally { setDrafting(false); }
  };

  const submit = async () => {
    const val = text.trim();
    if (!val || busy) return;
    setBusy(true);
    try {
      if (active.key === "reply") {
        if (!canSend) { toast.error("Connect Gmail to reply from here"); onReconnect?.(); return; }
        await api.post(`/email/events/${replyTarget.id}/send`, { text: val });
        toast.success("Reply sent");
      } else if (active.key === "logmail") {
        await api.post(`/email/events/${replyTarget.id}/log-outbound`, { text: val });
        toast.success("Logged your reply");
      } else if (active.key === "note") {
        await api.post(`/comments`, { entity_type: "bid", entity_id: bidId, text: val });
        toast.success("Note added");
      } else {
        const r = await api.post(`/crm/activities`, { entity_type: "bid", entity_id: bidId, kind: active.key, subject: val });
        if (active.key !== "task" && r.data?.activity_id) {
          await api.post(`/crm/activities/${r.data.activity_id}/done`).catch(() => {});
        }
        toast.success(active.key === "task" ? "Task added" : `${active.label} logged`);
      }
      setText("");
      onPosted?.();
    } catch (e) {
      if (e.response?.status === 403 && active.key === "reply") { toast.error("Reconnect Gmail to send"); onReconnect?.(); }
      else toast.error(e.response?.data?.detail || "Could not post that");
    } finally { setBusy(false); }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } };

  return (
    <div className="pt-2" data-testid="ctx-composer">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-2">
        <div className="flex items-center gap-1 px-1 pb-2 overflow-x-auto">
          {available.map((m) => {
            const on = m.key === active.key;
            return (
              <button key={m.key} onClick={() => setMode(m.key)} data-testid={`ctx-composer-mode-${m.key}`}
                className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2.5 py-1 shrink-0 transition ${on ? "bg-[#003E87] text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                <m.Icon className="w-3 h-3" /> {m.label}
              </button>
            );
          })}
          {active.key === "reply" && !claudeAssistant && (
            <button onClick={() => draftWithSol()} disabled={drafting} data-testid="ctx-composer-draft"
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-[#003E87] hover:underline disabled:opacity-50 shrink-0">
              <Sparkles className="w-3 h-3" /> {drafting ? "Drafting…" : "Draft a reply"}
            </button>
          )}
        </div>
        {active.key === "reply" && (
          <div className="flex items-center gap-1.5 px-1 pb-2 flex-wrap">
            <span className="text-[11px] text-slate-400 mr-0.5">Tone</span>
            {TONES.map((t) => (
              <button key={t.key} data-testid={`ctx-composer-tone-${t.key}`} disabled={drafting}
                onClick={() => draftWithSol(t.key)}
                className={`text-[11px] font-medium rounded-full px-2.5 py-0.5 transition disabled:opacity-50 ${tone === t.key ? "bg-[#003E87] text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                {t.label}
              </button>
            ))}
            <button data-testid="ctx-composer-preview-toggle" onClick={() => setShowPreview((v) => !v)}
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-[#003E87] hover:underline">
              <Eye className="w-3 h-3" /> {showPreview ? "Edit" : "Preview"}
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          {showPreview && active.key === "reply" ? (
            <div data-testid="ctx-composer-preview"
              className="flex-1 max-h-40 overflow-auto text-sm px-3 py-2 rounded-xl bg-slate-50 border border-slate-200"
              dangerouslySetInnerHTML={{ __html: previewHtml(text, sig) }} />
          ) : (
            <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} rows={1}
              data-testid="ctx-composer-input" placeholder={active?.placeholder}
              className="flex-1 resize-none max-h-32 text-sm px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:border-[#003E87] outline-none" />
          )}
          <button onClick={submit} disabled={busy || !text.trim()} data-testid="ctx-composer-send"
            title={`Post ${active.label.toLowerCase()}`}
            className="shrink-0 w-9 h-9 grid place-items-center rounded-full bg-[#003E87] text-white disabled:opacity-40 hover:bg-[#00306a] transition">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
